import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Calendar, Thermometer, ToggleLeft, ToggleRight, DollarSign, Target, AlertTriangle, AlertCircle } from 'lucide-react';
import { MetricCard } from './MetricCard';
import { IncomeVsCOGSChart } from './IncomeVsCOGSChart';
import { ProfitabilityChart } from './ProfitabilityChart';
import { SeasonalAnalysis } from './SeasonalAnalysis';
import { QuarterlyBreakdown } from './QuarterlyBreakdown';
import { YearlyComparison } from './YearlyComparison';
import { ComprehensiveTrendChart } from './ComprehensiveTrendChart';
import { MovingAverageChart } from './MovingAverageChart';
import { supabase } from '../lib/supabase';
import type { FinancialData } from '../data/financialData';
import { useUser } from '@clerk/clerk-react';
import { useDeal } from '../contexts/DealContext';

export function LenderSellerAnalysis() {
  const { user } = useUser();
  const { selectedDeal } = useDeal();
  const role = (user?.publicMetadata as Record<string, unknown> | undefined)?.role as string | undefined;
  const [chartViewMode, setChartViewMode] = useState<'monthly' | 'yearly'>('monthly');
  const [financialData, setFinancialData] = useState<FinancialData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function getQuarter(monthIndex: number) {
    if (monthIndex <= 2) return 'Q1';
    if (monthIndex <= 5) return 'Q2';
    if (monthIndex <= 8) return 'Q3';
    return 'Q4';
  }

  function getSeason(monthIndex: number): 'Winter' | 'Spring' | 'Summer' | 'Fall' {
    if (monthIndex === 11 || monthIndex <= 1) return 'Winter';
    if (monthIndex <= 4) return 'Spring';
    if (monthIndex <= 7) return 'Summer';
    return 'Fall';
  }

  function monthLabelFromIndex(monthIndex: number, year: number) {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[monthIndex]} ${year}`;
  }

  async function loadFinancialData(dealId?: string) {
    try {
      setLoading(true);
      setError(null);
      const transformed: FinancialData[] = [];

      if (dealId) {
        // 1) Try pnl_data for this deal
        const { data: pnlRows } = await supabase
          .from('pnl_data')
          .select('*')
          .eq('deal_id', dealId)
          .order('year', { ascending: true });

        
        if (pnlRows && pnlRows.length > 0) {
          for (const row of pnlRows as any[]) {
            const year: number | undefined = row.year ?? (row.statement_date ? new Date(row.statement_date).getFullYear() : undefined);
            const month: number | undefined = row.month;
            
            if (typeof year === 'number' && typeof month === 'number') {
              const dateObj = new Date(year, month - 1, 1); // month is 1-12, Date constructor expects 0-11
              transformed.push({
                month: monthLabelFromIndex(month - 1, year), // month - 1 for 0-based index
                date: dateObj,
                totalIncome: Number(row.total_revenue ?? 0),
                totalCOGS: Number(row.total_cogs ?? 0),
                grossProfit: Number(row.gross_profit ?? 0),
                totalExpense: Number(row.total_operating_expenses ?? 0),
                netOrdinaryIncome: Number(row.net_income ?? 0),
                quarter: getQuarter(month - 1), // month - 1 for 0-based index
                season: getSeason(month - 1), // month - 1 for 0-based index
                year,
                sde: row.sde != null ? Number(row.sde) : undefined,
                sdeBreakdown: row.sde_breakdown ?? undefined,
              });
            }
          }
        }

        // 2) If no pnl_data rows, build SAMPLE_PNL from seller_business_financials
        if (transformed.length === 0) {
          const { data: sbfRows } = await supabase
            .from('seller_business_financials')
            .select('*')
            .eq('deal_id', dealId)
            .order('year', { ascending: true });

          if (sbfRows && sbfRows.length > 0) {
            for (const row of sbfRows as any[]) {
              const year: number | undefined = row.year ?? (row.period_start ? new Date(row.period_start).getFullYear() : undefined);
              const monthly: any[] | undefined = row.monthly_financials ?? undefined;
              if (Array.isArray(monthly) && typeof year === 'number') {
                monthly.forEach((m, idx) => {
                  if (m) {
                    const dateObj = new Date(year, idx, 1);
                    transformed.push({
                      month: monthLabelFromIndex(idx, year),
                      date: dateObj,
                      totalIncome: Number(m.total_income ?? 0),
                      totalCOGS: Number(m.total_cogs ?? 0),
                      grossProfit: Number(m.gross_profit ?? 0),
                      totalExpense: Number(m.total_expense ?? 0),
                      netOrdinaryIncome: Number(m.net_ordinary_income ?? 0),
                      quarter: getQuarter(idx),
                      season: getSeason(idx),
                      year,
                      sde: m.sde != null ? Number(m.sde) : undefined,
                      sdeBreakdown: m.sde_breakdown ?? undefined,
                    });
                  }
                });
              }
            }
          }
        }
      }

      setFinancialData(transformed);
    } catch (e: any) {
      console.error('loadFinancialData (analysis) error:', e);
      setError(null);
      setFinancialData([]);
    } finally {
      setLoading(false);
    }
  }

  // Load data when deal changes (declare hooks before any conditional returns)
  useEffect(() => {
    if (selectedDeal?.deal_id) {
      loadFinancialData(selectedDeal.deal_id);
    }
  }, [selectedDeal?.deal_id]);

  // SECURITY: Block read-only users from accessing Lender/Seller Analysis
  // This page should NEVER be accessible to users who signed NDAs
  if (selectedDeal?.access_type === 'read') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            Lender/Seller Analysis is not available for read-only users. This page is restricted to deal creators and administrators.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading financial analysis...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error Loading Data</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!financialData || financialData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-xl mb-4">📊 No Data Available</div>
          <p className="text-gray-600">No financial data found in the database.</p>
        </div>
      </div>
    );
  }

  // Calculate key metrics for analysis
  const latestData = financialData[financialData.length - 1];
  const data2025 = financialData.filter(item => item.year === 2025);
  const data2024 = financialData.filter(item => item.year === 2024);
  const priorYearsData = financialData.filter(item => item.year < 2025);
  
  // Calculate monthly averages from all prior years (2020-2024)
  const monthlyAverages: { [key: string]: { income: number; cogs: number; grossProfit: number; netIncome: number; count: number } } = {};
  
  priorYearsData.forEach(item => {
    const monthName = item.month.split(' ')[0];
    if (!monthlyAverages[monthName]) {
      monthlyAverages[monthName] = { income: 0, cogs: 0, grossProfit: 0, netIncome: 0, count: 0 };
    }
    monthlyAverages[monthName].income += item.totalIncome;
    monthlyAverages[monthName].cogs += item.totalCOGS;
    monthlyAverages[monthName].grossProfit += item.grossProfit;
    monthlyAverages[monthName].netIncome += item.netOrdinaryIncome;
    monthlyAverages[monthName].count += 1;
  });
  
  // Calculate average values for each month
  Object.keys(monthlyAverages).forEach(month => {
    const data = monthlyAverages[month];
    data.income = data.income / data.count;
    data.cogs = data.cogs / data.count;
    data.grossProfit = data.grossProfit / data.count;
    data.netIncome = data.netIncome / data.count;
  });
  
  // Calculate projected 2025 metrics
  const months2025Available = data2025.length;
  const totalIncome2025Actual = data2025.reduce((sum, item) => sum + item.totalIncome, 0);
  const totalCOGS2025Actual = data2025.reduce((sum, item) => sum + item.totalCOGS, 0);
  const totalGrossProfit2025Actual = data2025.reduce((sum, item) => sum + item.grossProfit, 0);
  const totalNetIncome2025Actual = data2025.reduce((sum, item) => sum + item.netOrdinaryIncome, 0);
  const totalExpense2025Actual = data2025.reduce((sum, item) => sum + item.totalExpense, 0);
  
  // Project remaining months (Jun-Dec) using historical averages
  const remainingMonths = ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const projectedRemaining = remainingMonths.reduce((acc, month) => {
    const avg = monthlyAverages[month];
    if (avg) {
      acc.income += avg.income;
      acc.cogs += avg.cogs;
      acc.grossProfit += avg.grossProfit;
      acc.netIncome += avg.netIncome;
      const monthData = priorYearsData.filter(item => item.month.split(' ')[0] === month);
      const avgExpense = monthData.length > 0 ? monthData.reduce((sum, item) => sum + item.totalExpense, 0) / monthData.length : 0;
      acc.expense += avgExpense;
    }
    return acc;
  }, { income: 0, cogs: 0, grossProfit: 0, netIncome: 0, expense: 0 });
  
  // Total projected 2025 = Actual (Jan-May) + Projected (Jun-Dec)
  const totalIncome2025Projected = totalIncome2025Actual + projectedRemaining.income;
  const totalCOGS2025Projected = totalCOGS2025Actual + projectedRemaining.cogs;
  const totalGrossProfit2025Projected = totalGrossProfit2025Actual + projectedRemaining.grossProfit;
  const totalNetIncome2025Projected = totalNetIncome2025Actual + projectedRemaining.netIncome;
  const totalExpense2025Projected = totalExpense2025Actual + projectedRemaining.expense;
  
  // Get full year 2024 for YoY comparison
  const totalIncome2024Full = data2024.reduce((sum, item) => sum + item.totalIncome, 0);
  const totalCOGS2024Full = data2024.reduce((sum, item) => sum + item.totalCOGS, 0);
  const totalGrossProfit2024Full = data2024.reduce((sum, item) => sum + item.grossProfit, 0);
  const totalNetIncome2024Full = data2024.reduce((sum, item) => sum + item.netOrdinaryIncome, 0);
  const totalExpense2024Full = data2024.reduce((sum, item) => sum + item.totalExpense, 0);
  
  // Calculate key ratios for lending analysis
  const grossMargin2025 = totalIncome2025Projected !== 0 ? (totalGrossProfit2025Projected / totalIncome2025Projected) * 100 : 0;
  const netMargin2025 = totalIncome2025Projected !== 0 ? (totalNetIncome2025Projected / totalIncome2025Projected) * 100 : 0;
  const expenseRatio2025 = totalIncome2025Projected !== 0 ? (totalExpense2025Projected / totalIncome2025Projected) * 100 : 0;
  
  // Calculate YoY percentage changes
  const incomeYoY = totalIncome2024Full !== 0 ? ((totalIncome2025Projected - totalIncome2024Full) / Math.abs(totalIncome2024Full)) * 100 : 0;
  const grossProfitYoY = totalGrossProfit2024Full !== 0 ? ((totalGrossProfit2025Projected - totalGrossProfit2024Full) / Math.abs(totalGrossProfit2024Full)) * 100 : 0;
  const netIncomeYoY = totalNetIncome2024Full !== 0 ? ((totalNetIncome2025Projected - totalNetIncome2024Full) / Math.abs(totalNetIncome2024Full)) * 100 : 0;

  return (
    <div className="py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8">
      {/* Role-specific Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-blue-600" />
          {role === 'Lender' ? 'Lender Financial Analysis' : 'Seller Financial Analysis'}
        </h2>
        <p className="text-gray-600">
          {role === 'Lender' 
            ? 'Comprehensive financial analysis for lending decision support'
            : 'Detailed financial performance analysis for business valuation'
          }
        </p>
      </div>

      {/* Key Financial Ratios */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-green-600" />
          Key Financial Ratios (2025 Projected)
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          <MetricCard
            title="Gross Margin"
            value={grossMargin2025}
            format="percentage"
            className="border-l-4 border-l-green-500"
            sublabel={`Gross Profit: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalGrossProfit2025Projected)}`}
          />
          <MetricCard
            title="Net Margin"
            value={netMargin2025}
            format="percentage"
            className="border-l-4 border-l-blue-500"
            sublabel={`Net Income: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalNetIncome2025Projected)}`}
          />
          <MetricCard
            title="Expense Ratio"
            value={expenseRatio2025}
            format="percentage"
            className="border-l-4 border-l-amber-500"
            invertTrend={true}
            sublabel={`Total Expenses: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalExpense2025Projected)}`}
          />
        </div>
      </div>

      {/* Year-over-Year Performance */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          Year-over-Year Performance (2025 vs 2024)
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          2025 figures combine {months2025Available} months of actual data (Jan-May) with projections for Jun-Dec 
          based on historical monthly averages from prior years.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          <MetricCard
            title="Revenue Growth"
            value={incomeYoY}
            format="percentage"
            className="border-l-4 border-l-blue-500"
            sublabel={`2025: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalIncome2025Projected)}`}
          />
          <MetricCard
            title="Gross Profit Growth"
            value={grossProfitYoY}
            format="percentage"
            className="border-l-4 border-l-green-500"
            sublabel={`2025: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalGrossProfit2025Projected)}`}
          />
          <MetricCard
            title="Net Income Growth"
            value={netIncomeYoY}
            format="percentage"
            className="border-l-4 border-l-purple-500"
            sublabel={`2025: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalNetIncome2025Projected)}`}
          />
        </div>
      </div>

      {/* Risk Assessment */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          Risk Assessment & Key Metrics
        </h3>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Revenue Stability</h4>
              <p className="text-sm text-blue-800">
                {incomeYoY > 10 ? 'Strong growth trajectory with consistent revenue increases.' :
                 incomeYoY > 0 ? 'Moderate growth with stable revenue patterns.' :
                 'Revenue growth has slowed; monitor for trend reversal.'}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2">Profitability Health</h4>
              <p className="text-sm text-green-800">
                {netMargin2025 > 15 ? 'Excellent profitability with strong margins.' :
                 netMargin2025 > 10 ? 'Good profitability with healthy margins.' :
                 netMargin2025 > 5 ? 'Moderate profitability; monitor expense control.' :
                 'Low profitability; requires cost optimization.'}
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold text-purple-900 mb-2">Seasonal Patterns</h4>
              <p className="text-sm text-purple-800">
                Business shows seasonal variations with peak performance typically in summer months.
                Consider seasonal cash flow planning for lending decisions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Chart View Toggle */}
      <div className="mb-6 flex justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Chart View:</span>
            <div className="flex items-center gap-3">
              <span className={`text-sm ${chartViewMode === 'monthly' ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setChartViewMode(chartViewMode === 'monthly' ? 'yearly' : 'monthly')}
                className="flex items-center p-1 rounded-lg hover:bg-gray-50 transition-colors"
                aria-label={`Switch to ${chartViewMode === 'monthly' ? 'yearly' : 'monthly'} view`}
              >
                {chartViewMode === 'monthly' ? (
                  <ToggleLeft className="w-8 h-8 text-gray-400" />
                ) : (
                  <ToggleRight className="w-8 h-8 text-blue-600" />
                )}
              </button>
              <span className={`text-sm ${chartViewMode === 'yearly' ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                Yearly
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Comprehensive Financial Charts */}
      <div className="mb-8">
        <IncomeVsCOGSChart viewMode={chartViewMode} data={financialData} />
      </div>

      <div className="mb-8">
        <ProfitabilityChart viewMode={chartViewMode} data={financialData} />
      </div>

      <div className="mb-8">
        <ComprehensiveTrendChart viewMode={chartViewMode} data={financialData} />
      </div>

      <div className="mb-8">
        <MovingAverageChart viewMode={chartViewMode} data={financialData} />
      </div>

      {/* Seasonal and Quarterly Analysis */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
        <SeasonalAnalysis data={financialData} />
        <QuarterlyBreakdown data={financialData} />
      </div>

      {/* Year-over-Year Comparison */}
      <div className="mb-8">
        <YearlyComparison data={financialData} />
      </div>

      {/* Role-specific Recommendations */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-600" />
          {role === 'Lender' ? 'Lending Recommendations' : 'Valuation Insights'}
        </h3>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {role === 'Lender' ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-2">Credit Assessment</h4>
                <p className="text-sm text-green-800">
                  Based on {months2025Available} months of 2025 data and historical trends, 
                  the business shows {incomeYoY > 0 ? 'positive' : 'negative'} revenue growth 
                  with a {netMargin2025.toFixed(1)}% net margin. 
                  {netMargin2025 > 10 ? ' Strong profitability supports debt service capacity.' : 
                   netMargin2025 > 5 ? ' Moderate profitability; consider conservative loan terms.' :
                   ' Low profitability requires careful risk assessment.'}
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Cash Flow Analysis</h4>
                <p className="text-sm text-blue-800">
                  Projected 2025 net income of ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalNetIncome2025Projected)} 
                  provides {totalNetIncome2025Projected > 500000 ? 'strong' : totalNetIncome2025Projected > 300000 ? 'adequate' : 'limited'} 
                  cash flow for debt service. Consider seasonal variations in cash flow planning.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">Business Valuation</h4>
                <p className="text-sm text-purple-800">
                  With projected 2025 revenue of ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalIncome2025Projected)} 
                  and net income of ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalNetIncome2025Projected)}, 
                  the business demonstrates {incomeYoY > 0 ? 'growth potential' : 'stability'} 
                  with a {grossMargin2025.toFixed(1)}% gross margin.
                </p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg">
                <h4 className="font-semibold text-amber-900 mb-2">Market Position</h4>
                <p className="text-sm text-amber-800">
                  The seasonal nature of the business and consistent profitability over multiple years 
                  indicates a stable market position. Historical data shows resilience through various market conditions.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
